import type { JobFit } from '../lib/types';
import { label } from '../lib/format';

/**
 * The verdict sheet behind a posting's place in a ranking.
 *
 * There is no score to explain, which is the point. `AGENTS.md` forbids an
 * opaque aggregate ranking and asks that the components and the evidence stay
 * visible; here the components *are* the result, because the corpus cannot
 * support a number. Measured across the open postings, four of the seven
 * criteria are stated on under a quarter of them, so an aggregate would be
 * reporting `function` alone for the median posting.
 *
 * The three counts are shown side by side and never added together. "Two of
 * five, three unstated" is a different posting from "two of five, three
 * refused", and any single figure collapsing them says neither.
 *
 * Behind a `<details>`, like the momentum breakdown: a ranking is a ranking
 * first, and a page of postings each unfolding seven criteria is a wall.
 */

// Ordered so what the employer answered is read before what they did not, and
// what was never asked sinks to the bottom. `not_requested` is not a result;
// it is the profile declining to ask, and it is shown so an empty-looking
// sheet can be told apart from an unconfigured profile.
const VERDICT_ORDER: Record<string, number> = {
  unmet: 0,
  met: 1,
  not_stated: 2,
  not_requested: 3,
};

const VERDICT_TEXT: Record<string, string> = {
  met: 'Matches',
  unmet: 'Does not match',
  not_stated: 'Employer did not say',
  not_requested: 'Not asked',
};

export function FitBreakdown({ fit }: { fit: JobFit }) {
  const components = [...fit.components].sort(
    (a, b) => (VERDICT_ORDER[a.verdict] ?? 9) - (VERDICT_ORDER[b.verdict] ?? 9),
  );
  return (
    <details className="breakdown">
      <summary>
        Why this rank
        <span className="fit-counts">
          <b className="fit-count fit-count--met">{fit.met} match</b>
          <b className="fit-count fit-count--unmet">{fit.unmet} do not</b>
          {/* Kept separate from the two above rather than folded into a
              percentage: how much a posting matches and how much the employer
              stated are different questions, and one number answers neither. */}
          <b className="fit-count fit-count--unstated">{fit.notStated} unstated</b>
        </span>
      </summary>
      <ul className="breakdown__criteria">
        {components.map((component) => (
          <li key={component.criterion} className={`verdict verdict--${component.verdict}`}>
            <span>{label(component.criterion)}</span>
            <small className="muted">{VERDICT_TEXT[component.verdict] ?? component.verdict}</small>
            {/* The employer's own words where they said anything, so a reader
                can overrule a rule fitted to four employers' boilerplate. */}
            <em className="verdict__basis">{component.basis}</em>
          </li>
        ))}
      </ul>
    </details>
  );
}
